import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import DateInput from './DateInput';
import { Plus, Trash2, TrendingUp, Calendar, Briefcase } from 'lucide-react';

const UPLIFT_TYPES = [
  { value: 'christmas', label: 'Christmas' },
  { value: 'new_year', label: 'New Year' },
  { value: 'hardship', label: 'Hardship' },
  { value: 'hazard', label: 'Hazard Pay' },
  { value: 'weekend', label: 'Weekend Premium' },
  { value: 'offshore', label: 'Offshore Allowance' },
  { value: 'other', label: 'Other' },
];

const UpliftSection = ({ uplifts, addUplift, updateUplift, removeUplift, dateContext }) => {
  const [hasUplifts, setHasUplifts] = useState(uplifts.length > 0);

  const handleToggle = (val) => {
    setHasUplifts(val);
    if (val && uplifts.length === 0) {
      addUplift();
    }
  };

  return (
    <div className="space-y-3" data-testid="uplift-section">
      <div className="flex items-center justify-between">
        <Label className="text-slate-300 flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-amber-400" />
          Were any uplifts applied during this trip?
        </Label>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant={hasUplifts ? 'default' : 'outline'}
            onClick={() => handleToggle(true)}
            className={hasUplifts ? 'bg-amber-600 hover:bg-amber-700 h-7 px-3' : 'border-slate-600 text-slate-400 h-7 px-3'}
            data-testid="uplifts-yes-btn"
          >
            Yes
          </Button>
          <Button
            type="button"
            size="sm"
            variant={!hasUplifts ? 'default' : 'outline'}
            onClick={() => handleToggle(false)}
            className={!hasUplifts ? 'bg-slate-600 hover:bg-slate-500 h-7 px-3' : 'border-slate-600 text-slate-400 h-7 px-3'}
            data-testid="uplifts-no-btn"
          >
            No
          </Button>
        </div>
      </div>

      {hasUplifts && (
        <div className="space-y-3 pl-2 border-l-2 border-amber-500/30">
          {uplifts.map((uplift, index) => (
            <div key={uplift._key || index} className="bg-slate-900/50 rounded-lg p-3 border border-slate-700/50 space-y-3" data-testid={`uplift-entry-${index}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400 font-medium">Uplift {index + 1}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeUplift(index)}
                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  data-testid={`remove-uplift-${index}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Type</Label>
                  <select
                    value={uplift.type || 'other'}
                    onChange={(e) => {
                      const type = e.target.value;
                      const typeLabel = UPLIFT_TYPES.find(t => t.value === type)?.label || '';
                      updateUplift(index, 'type', type);
                      if (type !== 'other') {
                        updateUplift(index, 'name', typeLabel);
                      }
                    }}
                    className="w-full h-9 rounded-md bg-slate-900 border border-slate-600 text-white text-sm px-2"
                    data-testid={`uplift-type-${index}`}
                  >
                    {UPLIFT_TYPES.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Amount (£/day)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={uplift.amount || ''}
                    onChange={(e) => updateUplift(index, 'amount', e.target.value)}
                    placeholder="e.g. 150"
                    className="bg-slate-900 border-slate-600 text-white h-9"
                    data-testid={`uplift-amount-${index}`}
                  />
                </div>
              </div>

              {uplift.type === 'other' && (
                <div className="space-y-1">
                  <Label className="text-slate-400 text-xs">Custom Label</Label>
                  <Input
                    type="text"
                    value={uplift.name || ''}
                    onChange={(e) => updateUplift(index, 'name', e.target.value)}
                    placeholder="e.g. Australian Waters"
                    className="bg-slate-900 border-slate-600 text-white h-9"
                    data-testid={`uplift-name-${index}`}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-slate-400 text-xs">Applies to</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant={uplift.applies_to === 'whole_trip' ? 'default' : 'outline'}
                    onClick={() => {
                      updateUplift(index, 'applies_to', 'whole_trip');
                      updateUplift(index, 'date_from', '');
                      updateUplift(index, 'date_to', '');
                    }}
                    className={uplift.applies_to === 'whole_trip'
                      ? 'bg-amber-600/80 hover:bg-amber-700 h-7 px-3 text-xs'
                      : 'border-slate-600 text-slate-400 h-7 px-3 text-xs'}
                    data-testid={`uplift-whole-trip-${index}`}
                  >
                    <Briefcase className="h-3 w-3 mr-1" />
                    Whole Trip
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={uplift.applies_to === 'specific_dates' ? 'default' : 'outline'}
                    onClick={() => updateUplift(index, 'applies_to', 'specific_dates')}
                    className={uplift.applies_to === 'specific_dates'
                      ? 'bg-amber-600/80 hover:bg-amber-700 h-7 px-3 text-xs'
                      : 'border-slate-600 text-slate-400 h-7 px-3 text-xs'}
                    data-testid={`uplift-specific-dates-${index}`}
                  >
                    <Calendar className="h-3 w-3 mr-1" />
                    Specific Dates
                  </Button>
                </div>

                {uplift.applies_to === 'specific_dates' && (
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <div className="space-y-1">
                      <Label className="text-slate-400 text-xs">From</Label>
                      <DateInput
                        contextDate={dateContext}
                        value={uplift.date_from || ''}
                        onChange={(e) => updateUplift(index, 'date_from', e.target.value)}
                        className="bg-slate-900 border-slate-600 text-white h-9"
                        data-testid={`uplift-date-from-${index}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-slate-400 text-xs">To</Label>
                      <DateInput
                        contextDate={dateContext}
                        value={uplift.date_to || ''}
                        onChange={(e) => updateUplift(index, 'date_to', e.target.value)}
                        className="bg-slate-900 border-slate-600 text-white h-9"
                        data-testid={`uplift-date-to-${index}`}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addUplift}
            className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 w-full h-8"
            data-testid="add-uplift-btn"
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Another Uplift
          </Button>
        </div>
      )}
    </div>
  );
};

export default UpliftSection;
